import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, DollarSign, RefreshCw, Search, TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';

const EXPENSE_CATEGORIES = [
  { value: 'rent', label: 'Rent' },
  { value: 'medical_supplies', label: 'Medical Supplies' },
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'software_subscriptions', label: 'Software Subscriptions' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'bank_fees', label: 'Bank Fees' },
  { value: 'travel', label: 'Travel' },
  { value: 'meals', label: 'Meals' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'e_transfer', label: 'E-Transfer' },
  { value: 'other', label: 'Other' },
];

const TRANSACTION_TYPES = [
  { value: 'expense', label: 'Expense' },
  { value: 'income', label: 'Income' },
];

export default function FinanceTransactions() {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Filters - use "all" instead of empty string for Select compatibility
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchVendor, setSearchVendor] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    description: '',
    amount: '',
    transaction_type: 'expense',
    category: 'other',
    payment_method: 'debit',
    notes: ''
  });

  useEffect(() => {
    fetchTransactions();
  }, [filterYear, filterMonth, filterCategory]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear) params.append('year', filterYear);
      if (filterMonth && filterMonth !== 'all') params.append('month', filterMonth);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (searchVendor) params.append('vendor', searchVendor);
      
      const res = await api.get(`/finance/transactions?${params.toString()}`);
      setTransactions(res.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!form.date || !form.vendor || !form.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await api.post('/finance/transactions', {
        ...form,
        amount: parseFloat(form.amount)
      });
      
      toast.success('Transaction added');
      setShowAddDialog(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transaction');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    try {
      await api.patch(`/finance/transactions/${editingTransaction.id}`, {
        ...form,
        amount: parseFloat(form.amount)
      });
      
      toast.success('Transaction updated');
      setShowEditDialog(false);
      setEditingTransaction(null);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const handleDelete = async (txn) => {
    if (!confirm('Delete this transaction?')) return;
    
    try {
      await api.delete(`/finance/transactions/${txn.id}`);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const openEditDialog = (txn) => {
    setEditingTransaction(txn);
    setForm({
      date: txn.date,
      vendor: txn.vendor,
      description: txn.description,
      amount: txn.amount.toString(),
      transaction_type: txn.transaction_type,
      category: txn.category,
      payment_method: txn.payment_method,
      notes: txn.notes || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      description: '',
      amount: '',
      transaction_type: 'expense',
      category: 'other',
      payment_method: 'debit',
      notes: ''
    });
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      rent: 'bg-amber-100 text-amber-700',
      medical_supplies: 'bg-red-100 text-red-700',
      office_supplies: 'bg-blue-100 text-blue-700',
      utilities: 'bg-orange-100 text-orange-700',
      insurance: 'bg-cyan-100 text-cyan-700',
      professional_fees: 'bg-purple-100 text-purple-700',
      payroll: 'bg-emerald-100 text-emerald-700',
      revenue: 'bg-green-100 text-green-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  // Calculate totals
  const totalIncome = transactions.filter(t => t.transaction_type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = transactions.filter(t => t.transaction_type === 'expense').reduce((sum, t) => sum + t.amount, 0);

  const TransactionForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date">Date *</Label>
          <Input
            id="date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="transaction_type">Type *</Label>
          <Select 
            value={form.transaction_type} 
            onValueChange={(v) => setForm({ ...form, transaction_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSACTION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label htmlFor="vendor">Vendor *</Label>
        <Input
          id="vendor"
          value={form.vendor}
          onChange={(e) => setForm({ ...form, vendor: e.target.value })}
          placeholder="e.g., Amazon, Bell Canada"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="description">Description *</Label>
        <Input
          id="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Brief description of the transaction"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Amount ($) *</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="0.00"
            required
          />
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Select 
            value={form.category} 
            onValueChange={(v) => setForm({ ...form, category: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPENSE_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label htmlFor="payment_method">Payment Method</Label>
        <Select 
          value={form.payment_method} 
          onValueChange={(v) => setForm({ ...form, payment_method: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_METHODS.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional notes..."
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => {
          setShowAddDialog(false);
          setShowEditDialog(false);
          resetForm();
        }}>
          Cancel
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-4" data-testid="finance-transactions">
      {/* Summary Bar */}
      <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <span className="text-sm">Income: <strong className="text-emerald-600">${totalIncome.toLocaleString()}</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-500" />
          <span className="text-sm">Expenses: <strong className="text-red-600">${totalExpenses.toLocaleString()}</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-blue-500" />
          <span className="text-sm">Net: <strong className={totalIncome - totalExpenses >= 0 ? 'text-blue-600' : 'text-amber-600'}>
            ${(totalIncome - totalExpenses).toLocaleString()}
          </strong></span>
        </div>
      </div>

      {/* Filters and Add Button */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {EXPENSE_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search vendor..."
              value={searchVendor}
              onChange={(e) => setSearchVendor(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTransactions()}
              className="pl-10 w-[180px]"
            />
          </div>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-transaction-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Transaction</DialogTitle>
              <DialogDescription>
                Manually enter a new transaction.
              </DialogDescription>
            </DialogHeader>
            <TransactionForm onSubmit={handleAdd} submitLabel="Add Transaction" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Transactions Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-12">
          <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No transactions found</p>
          <p className="text-sm text-slate-400">Add transactions manually or extract from documents</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((txn) => (
                <TableRow key={txn.id} data-testid={`transaction-row-${txn.id}`}>
                  <TableCell className="text-slate-500">
                    {format(new Date(txn.date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="font-medium">{txn.vendor}</TableCell>
                  <TableCell className="text-slate-600 max-w-[200px] truncate">
                    {txn.description}
                  </TableCell>
                  <TableCell>
                    <Badge className={getCategoryBadgeColor(txn.category)}>
                      {txn.category.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    <span className={txn.transaction_type === 'income' ? 'text-emerald-600' : 'text-red-600'}>
                      {txn.transaction_type === 'income' ? '+' : '-'}${txn.amount.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(txn)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(txn)}
                        title="Delete"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEditDialog(false);
          setEditingTransaction(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
          </DialogHeader>
          <TransactionForm onSubmit={handleEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
